import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import {
  vdjColor, vdjLoop, vdjBeatBreath, vdjBarChroma, VdjPost, VdjComfort, VDJ_PRESETS
} from './lib/vdjLook.js';

// Halo - one big neon ring around the cover, in a slow smoke that drifts and
// catches its light. One shape, one mood. Rhythmic ambient, downtempo, slow
// R&B, neo-soul, film music. Applies the LOOK VDJ LOUNGE (lib/vdjLook.js,
// preset 'lounge').
//
// THE SMOKE HAS NO COLOUR OF ITS OWN. Two passes:
//  1. half resolution, the smoke: a 3-octave fbm (unrolled) advected by one
//     level of slow domain warp, drifting up and a little sideways, plus a
//     coarser, slower layer at 40 %. Its density is multiplied by the light
//     the ring sheds on that point:
//         light(p) = sum over N points of the ring of col(angle) / (1 + d^2 k)^2
//     (squared: with the brief's 1 / (1 + d^2 k) the sum along a ring
//     falls only as 1 / d and the smoke stayed lit into the corners -
//     measured, the first capture was pink to the edges)
//     so the smoke glows near the tube in the tube's local colour and is
//     black away from it; a curl of smoke that drifts past the ring lights
//     up, and goes out as it leaves.
//  2. full resolution, the composite: the smoke upscaled, the ring drawn by
//     its distance in PIXELS (a sharp 5 px white core, a coloured halo),
//     dimmed by the smoke in front of it (x 0.85 at full density).
//
// THE N POINTS TURN WITH THE PIXEL. A fixed set of 16 points makes 16 light
// bulbs: right at the tube the nearest point dominates and the smoke pulses
// from one to the next. Here the points sit at the pixel's own angle plus
// k x 360/N: the same quadrature of the ring for every pixel, with no
// preferred point, so the light is smooth along the ring and still carries
// the colour of the tube's nearby stretch. The distances are then a function
// of the radius only, d_k^2 = r^2 + R^2 - 2 r R cos(k 360/N), and each
// point's direction is the pixel's direction rotated by a constant: no trig
// per point.
//
// NOISE FROM A TEXTURE. One 256^2 RGBA random texture read with the smooth
// texel trick (iq): one fetch per octave, two channels for the warp's two
// components. 5 fetches with both layers (the brief's budget was 6 reads).
// The drift wraps modulo 512 texels: every octave's scale (0.5, 1, 2, 4)
// maps that onto a whole number of periods, so the wrap never shows.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'halo',
  family: 'shader',
  danceability: 0.45,
  requiresTier: 0,
  energyLevel: 0.32,
  bpmMin: 60,
  bpmMax: 110,
  movement: 0.30,
  tonality: 0.80,
  enabled: true
};

const LOUNGE = VDJ_PRESETS.lounge;

const TIER = {
  // scale: the smoke target's fraction of the frame (ridged filaments blur at half)
  0: { layers: 1, lights: 8, scale: 0.5 },
  1: { layers: 2, lights: 16, scale: 0.5 },    // 0.75 cost the Pi 15 ms (25.7 -> 40.6) for barely sharper wisps
  2: { layers: 2, lights: 16, scale: 0.75 }
};
const DEFAULT_TIER = 2;

const RING_OF_DIAG = 1.6;         // radius / the safe zone's half-diagonal
const RING_FIT = 0.94;            // the ring stays inside the frame's height
// The tube's radial profile, in px from its axis (corrections of 14/10):
//   core   0-2 px   warm white, luminance 1.1 - the only part that blooms
//   sheath 2-7 px   the local neon colour, saturated, luminance <= 0.7
//   halo   7-40 px  the local colour, exp(-d / 12) x 0.35, under the threshold
// No white beyond 2 px: the first version mixed white into a 5 px core and
// added a white-ish glow, and the ring read white with the colour only in
// the smoke.
const CORE_PX = 2.0;
const SHEATH_PX = 7.0;
const HALO_END_PX = 40.0;
const HALO_TAU_PX = 12.0;
const HALO_GAIN = 0.35;
// The brief asks for luminance 0.7; through ACES at exposure 1.1 a colour
// that bright comes out nearly white (measured: saturation 0.27 in the
// sheath). Its brightest channel is held at SHEATH_MAX instead, and its
// luminance at SHEATH_LUM at most.
const SHEATH_MAX = 0.55;
const SHEATH_LUM = 0.7;
const WHITE = [1.0, 0.95, 0.90];
const CORE_LUM = 1.1;
const GAP_DEG = 4;
const GAP_ANGLE = -40 * Math.PI / 180;   // lower right, away from the eye
const ELECTRODE_PX = 4.0;
const ELECTRODE_LUM = 1.2;
const MIN_HUE_GAP = 0.25;         // the two colours of a pair, at least this far apart
const TURN_BEATS = 32;
const BREATH = 0.06;
const WAVE_GAIN = 0.35;
const WAVE_WIDTH = 30;            // exp(-(1 - cos) x WAVE_WIDTH): ~ +-20 degrees
const TUBE_NOISE = 0.12;
const FLICKER = 0.10;             // bands.high
const SMOKE_SCALE = 4.0;          // noise texels per height unit (first octave)
const DRIFT_SPEED = 0.55;         // texels / s at bands.mid 0.5
const WIND_UP = Math.PI / 2;
const WIND_SIDE = 0.35;           // rad, the sideways lean
const LIGHT_K = 140;             // 1 / (1 + d^2 k)^2, d in height units
const LIGHT_CUT = [0.6, 0.2];     // x R from the tube: the light fades to nothing between them
const INNER_DENSITY = 0.3;        // smoke density kept inside 0.5 R of the centre
const WARP_AMP = 3.4;             // twice the first version's: filaments curl into whorls
const SMOKE_GAIN = 1.25;
const SAFE_FLOOR = 0.5;
const FLOOR_SQUASH = 0.10;
const FLOOR_ATT = 0.32;
const DROP_GROW_BARS = 2;
const DROP_HOLD_BARS = 8;
const RING2_SCALE = 1.3;
const FREE_BPM = 84;
const NOISE_SIZE = 256;
const WRAP = 512;
const LUM_W = 128, LUM_H = 72;
const LUM_EVERY = 12;

const DEFAULT_PAIRS = [['#ff2a8a', '#28d8ff'], ['#ffa030', '#8040ff'], ['#30ffb0', '#ff40c0'], ['#4070ff', '#ff6040']];

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const quadVertexShader = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const RING_COLOUR_CHUNK = `
  uniform vec3 uColA;
  uniform vec3 uColB;
  uniform vec2 uRot;         // the gradient's direction, turning once in 32 beats
  uniform vec2 uGapDir;
  uniform vec2 uWaveDir;
  uniform float uWaveA;
  // The ring's colour in direction u (a unit vector from the centre).
  vec3 ringColour(vec2 u) {
    vec3 c = mix(uColA, uColB, 0.5 + 0.5 * dot(u, uRot));
    // Between two distant hues the mix turns grey (orange + blue = a dull
    // mauve): take most of the grey out, keep the hue and the peak channel.
    float mx = max(c.r, max(c.g, c.b));
    c -= 0.8 * min(c.r, min(c.g, c.b));
    c *= mx / max(max(c.r, max(c.g, c.b)), 1e-3);
    float w = exp(-(1.0 - dot(u, uWaveDir)) * ${WAVE_WIDTH.toFixed(1)});
    return c * (1.0 + ${WAVE_GAIN.toFixed(2)} * uWaveA * w);
  }
`;

function smokeFragmentShader(layers, lights) {
  // The N points, unrolled: pixel direction rotated by k x 360/N.
  let sum = '';
  let norm = 0;
  const R0 = 0.8;
  for (let k = 0; k < lights; k++) {
    const a = (k - Math.floor(lights / 2)) * 2 * Math.PI / lights;
    const ca = Math.cos(a).toFixed(6), sa = Math.sin(a).toFixed(6);
    sum += `
    {
      vec2 u = vec2(dir.x * ${ca} - dir.y * ${sa}, dir.x * ${sa} + dir.y * ${ca});
      float d2 = r2 + R2 - 2.0 * rR * ${ca};
      float wk = 1.0 / (1.0 + d2 * uK);
      light += ringColour(u) * (wk * wk);
    }`;
    // normaliser: the sum of the weights on the ring itself (r = R), for a
    // typical radius, so the tube's own light is ~1 whatever N is
    const d2 = 2 * R0 * R0 * (1 - Math.cos(a));
    norm += 1 / Math.pow(1 + d2 * LIGHT_K, 2);
  }
  return `
  precision mediump float;
  uniform sampler2D uNoise;
  uniform float uAspect;
  uniform vec2 uCenter;
  uniform float uR;
  uniform highp vec2 uDrift;
  uniform highp vec2 uDrift2;
  uniform highp vec2 uWarpT;
  uniform float uDensity;
  uniform float uK;
  uniform float uMode;       // 0 normal, 1 light only, 2 no smoke
  ${RING_COLOUR_CHUNK}
  varying vec2 vUv;

  // Smooth value noise from one bilinear fetch (texel-centred, smoothstepped).
  vec4 tnoise(highp vec2 p) {
    highp vec2 i = floor(p);
    highp vec2 f = p - i;
    f = f * f * (3.0 - 2.0 * f);
    return texture2D(uNoise, (i + f + 0.5) / ${NOISE_SIZE.toFixed(1)});
  }

  void main() {
    vec2 p = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    // --- the light of the ring at p ---
    vec2 rel = p - uCenter;
    float r = length(rel);
    vec2 dir = r > 1e-4 ? rel / r : vec2(1.0, 0.0);
    float r2 = r * r, R2 = uR * uR, rR = r * uR;
    vec3 light = vec3(0.0);
    ${sum}
    light *= ${(1 / norm).toFixed(6)};
    // Far from the tube the smoke is BLACK: the light ends at 0.6 R from it.
    light *= smoothstep(${LIGHT_CUT[0].toFixed(2)} * uR, ${LIGHT_CUT[1].toFixed(2)} * uR, abs(r - uR));
    // --- the smoke's density ---
    highp vec2 q = p * ${SMOKE_SCALE.toFixed(1)};
    vec2 w = tnoise(q * 0.5 + uWarpT).rg - 0.5;
    highp vec2 qa = q + w * ${WARP_AMP.toFixed(1)} - uDrift;
    // RIDGED fbm: 1 - |2n - 1|, squared - filaments, not blobs.
    float n1 = 1.0 - abs(tnoise(qa).r * 2.0 - 1.0);
    float n2 = 1.0 - abs(tnoise(qa * 2.0).g * 2.0 - 1.0);
    float n3 = 1.0 - abs(tnoise(qa * 4.0).b * 2.0 - 1.0);
    float f = (n1 * n1) * 0.5714 + (n2 * n2) * 0.2857 + (n3 * n3) * 0.1429;
    // The hardware's bilinear weights have only a few bits between two texels:
    // magnified, ridged, squared and thresholded, they showed as contour
    // terraces. A dither of a fraction of one step breaks them.
    f += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) * 0.03;
    float dens = smoothstep(0.35, 0.75, f);
    ${layers > 1 ? `
    float g = tnoise(q * 0.5 + w * 0.8 - uDrift2).a;
    dens += 0.4 * smoothstep(0.35, 0.80, g);` : ''}
    dens = clamp(dens * uDensity, 0.0, 1.0);
    // The inside of the ring, around the cover, stays dark.
    dens *= mix(${INNER_DENSITY.toFixed(2)}, 1.0, smoothstep(0.42 * uR, 0.5 * uR, r));
    if (uMode > 1.5) dens = 0.0;
    vec3 col = (uMode > 0.5 && uMode < 1.5 ? 1.0 : dens) * light * ${SMOKE_GAIN.toFixed(2)};
    gl_FragColor = vec4(col * 0.5, dens);
  }
`;
}

const compositeFragmentShader = `
  precision highp float;
  uniform sampler2D uSmoke;
  uniform sampler2D uNoise;
  uniform vec2 uCenter;
  uniform float uR;
  uniform float uR2;
  uniform float uRing2;
  uniform float uPxUnit;
  uniform float uIntensity;
  uniform float uTubeT;
  uniform float uFlicker;
  uniform float uFloor;
  uniform float uFloorY;
  uniform float uGain;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  ${RING_COLOUR_CHUNK}
  varying vec2 vUv;

  // Saturated colour at a luminance of at most SHEATH_LUM (a blue cannot
  // reach it: it stays at full saturation instead of turning white).
  vec3 sheathColour(vec3 c) {
    float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
    float m = max(c.r, max(c.g, c.b));
    return c * min(${SHEATH_MAX.toFixed(2)} / max(m, 1e-3), ${SHEATH_LUM.toFixed(2)} / max(l, 1e-3));
  }

  // The tube at radius R, by its distance in px from the axis: a white core,
  // a coloured sheath, a coloured halo. corePx / sheathPx / haloEnd set the
  // profile (the drop's second ring is thinner). gapOn: the electrodes.
  vec3 tube(vec2 p, float R, float corePx, float sheathPx, float haloEnd, float gapOn, out float coreOut) {
    vec2 rel = p - uCenter;
    float r = length(rel);
    vec2 u = r > 1e-4 ? rel / r : vec2(1.0, 0.0);
    float dPx = abs(r - R) * uPxUnit;
    vec3 c = sheathColour(ringColour(u));
    // Slow variation along the tube (read along the circle: an angle seams at 180 deg).
    float n = texture2D(uNoise, u * 0.004 + vec2(uTubeT, 0.37 + 0.01 * R)).r;
    float I = (1.0 + ${TUBE_NOISE.toFixed(2)} * (2.0 * n - 1.0)) * uFlicker;
    float core = 1.0 - smoothstep(corePx - 0.5, corePx + 0.5, dPx);
    float sheath = (1.0 - smoothstep(sheathPx - 0.6, sheathPx + 0.6, dPx)) * (1.0 - core);
    float halo = exp(-dPx / ${HALO_TAU_PX.toFixed(1)}) * ${HALO_GAIN.toFixed(2)}
               * smoothstep(sheathPx - 0.6, sheathPx + 0.6, dPx) * (1.0 - smoothstep(haloEnd - 10.0, haloEnd, dPx));
    // The electrodes: an arc of GAP_DEG ON the tube where core and sheath are
    // 0. Only along the arc: the halo is not cut (a radial cut of the halo
    // left a dark diagonal wedge running out from the ring).
    float cut = 1.0;
    if (gapOn > 0.5) {
      float gapHalfPx = R * uPxUnit * ${(GAP_DEG * Math.PI / 360).toFixed(5)};
      float along = length(u - uGapDir) * R * uPxUnit;
      cut = smoothstep(gapHalfPx - 0.7, gapHalfPx + 0.7, along);
    }
    vec3 white = vec3(${WHITE.map((v) => v.toFixed(2)).join(', ')}) * ${(CORE_LUM / (0.2126 * WHITE[0] + 0.7152 * WHITE[1] + 0.0722 * WHITE[2])).toFixed(3)};
    vec3 col = (white * core + c * sheath) * cut + c * halo;
    col *= I;
    coreOut = core * cut;
    if (gapOn > 0.5) {
      // a brighter point at each end of the cut, ON the ring
      float s = ${Math.sin(GAP_DEG * Math.PI / 360).toFixed(5)}, k = ${Math.cos(GAP_DEG * Math.PI / 360).toFixed(5)};
      vec2 e1 = uCenter + R * vec2(uGapDir.x * k - uGapDir.y * s, uGapDir.x * s + uGapDir.y * k);
      vec2 e2 = uCenter + R * vec2(uGapDir.x * k + uGapDir.y * s, -uGapDir.x * s + uGapDir.y * k);
      float d1 = length(p - e1) * uPxUnit, d2 = length(p - e2) * uPxUnit;
      float e = max(1.0 - smoothstep(${(ELECTRODE_PX - 0.7).toFixed(1)}, ${(ELECTRODE_PX + 0.7).toFixed(1)}, d1),
                    1.0 - smoothstep(${(ELECTRODE_PX - 0.7).toFixed(1)}, ${(ELECTRODE_PX + 0.7).toFixed(1)}, d2));
      col = mix(col, white * ${(ELECTRODE_LUM / CORE_LUM).toFixed(3)} * uFlicker, e);
    }
    return col;
  }

  void main() {
    vec2 p = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec4 sm = texture2D(uSmoke, vUv);
    vec3 smoke = sm.rgb * 2.0;
    vec2 q = abs(p - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    smoke *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    float core;
    vec3 ring = tube(p, uR, ${CORE_PX.toFixed(1)}, ${SHEATH_PX.toFixed(1)}, ${HALO_END_PX.toFixed(1)}, 1.0, core);
    // The smoke in front of the tube dims its core.
    ring *= mix(1.0, 0.85, sm.a * core);
    vec3 col = smoke + ring * uIntensity;
    if (uRing2 > 0.0) {
      float c2;
      col += tube(p, uR2, 1.0, 3.5, 24.0, 0.0, c2) * (uRing2 * 0.75 * uIntensity);
    }
    if (uFloor > 0.0 && p.y < uFloorY) {
      // The puddle: the ring mirrored in the floor and squashed into an
      // ellipse, blurred by its own distance, fading away from the edge.
      vec2 src = vec2(p.x, uFloorY + (uFloorY - p.y) / ${FLOOR_SQUASH.toFixed(2)});
      vec2 rel = src - uCenter;
      float r = length(rel);
      vec2 u = r > 1e-4 ? rel / r : vec2(1.0, 0.0);
      float dd = abs(r - uR) * ${FLOOR_SQUASH.toFixed(2)} * uPxUnit;
      float refl = exp(-dd / 5.0) * 0.8 + exp(-dd / 18.0) * 0.35;
      float fade = exp(-(uFloorY - p.y) / 0.10);
      col += ringColour(u) * refl * fade * ${FLOOR_ATT.toFixed(2)} * uFloor * uIntensity;
      // the floor's sheen at its edge
      col += mix(uColA, uColB, 0.5) * exp(-(uFloorY - p.y) * uPxUnit / 2.0) * 0.06 * uFloor;
    }
    col *= uGain;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Compiled once per (layers, lights) and never released; the uniforms live
// with the materials (three-caches-the-uniform-object).
const MATERIALS = new Map();
let NOISE_TEX = null;

function noiseTexture() {
  if (NOISE_TEX) return NOISE_TEX;
  const n = NOISE_SIZE * NOISE_SIZE * 4;
  const data = new Uint8Array(n);
  let s = 0x2f6b4a1d;
  for (let i = 0; i < n; i++) { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; data[i] = (s >>> 0) & 255; }
  const t = new THREE.DataTexture(data, NOISE_SIZE, NOISE_SIZE, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  NOISE_TEX = t;
  return t;
}

function materialsFor(layers, lights) {
  const key = layers + 'x' + lights;
  if (MATERIALS.has(key)) return MATERIALS.get(key);
  const ring = {
    uColA: { value: new THREE.Color() }, uColB: { value: new THREE.Color() },
    uRot: { value: new THREE.Vector2(1, 0) }, uGapDir: { value: new THREE.Vector2(Math.cos(GAP_ANGLE), Math.sin(GAP_ANGLE)) },
    uWaveDir: { value: new THREE.Vector2(0, 1) }, uWaveA: { value: 0 }
  };
  const geo = { uCenter: { value: new THREE.Vector2(0, 0.08) }, uR: { value: 0.8 } };
  const noise = { value: noiseTexture() };
  const su = Object.assign({
    uNoise: noise, uAspect: { value: 16 / 9 },
    uDrift: { value: new THREE.Vector2() }, uDrift2: { value: new THREE.Vector2() }, uWarpT: { value: new THREE.Vector2() },
    uDensity: { value: 1 }, uK: { value: LIGHT_K }, uMode: { value: 0 }
  }, ring, geo);
  const cu = Object.assign({
    uSmoke: { value: null }, uNoise: noise,
    uR2: { value: 1 }, uRing2: { value: 0 }, uPxUnit: { value: 360 }, uIntensity: { value: 1 },
    uTubeT: { value: 0 }, uFlicker: { value: 1 }, uFloor: { value: 0 }, uFloorY: { value: -0.9 }, uGain: { value: 1 },
    uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
    uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  }, ring, geo);
  const smoke = new THREE.ShaderMaterial({
    uniforms: su, vertexShader: quadVertexShader, fragmentShader: smokeFragmentShader(layers, lights),
    depthTest: false, depthWrite: false
  });
  const composite = new THREE.ShaderMaterial({
    uniforms: cu, vertexShader: quadVertexShader, fragmentShader: compositeFragmentShader,
    depthTest: false, depthWrite: false
  });
  const m = { smoke, composite, su, cu };
  MATERIALS.set(key, m);
  return m;
}

const _size = new THREE.Vector2();
const _ca = new THREE.Color();
const _cb = new THREE.Color();

export class HaloVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    this._mode = urlParam('halosmoke') === '0' ? 2 : (urlParam('halolight') === '1' ? 1 : 0);
    this._debugDrop = urlParam('halodrop') === '1';
    this._forcedFloor = urlParam('halofloor');
    // ?halobloom=0: no bloom (to measure the tube's own core)
    const hb = parseFloat(urlParam('halobloom'));
    // A weak bloom: the shared chain's bloom spreads the white core tens of
    // pixels in WHITE and leaves a veil over the whole frame (measured at 0.42:
    // the halo's saturation 0.22 at 10 px, background mean 8.7/255). The
    // coloured halo is drawn; the bloom only rims the core.
    this._post = new VdjPost({ bloomStrength: Number.isFinite(hb) ? hb : 0.12, bloomThreshold: 0.78 });
    this._comfort = new VdjComfort(LOUNGE.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const m = materialsFor(this._layout.layers, this._layout.lights);
    this._m = m; this._su = m.su; this._cu = m.cu;
    this._smokeTarget = new THREE.WebGLRenderTarget(4, 4, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType, depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._cu.uSmoke.value = this._smokeTarget.texture;
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._smokeScene = new THREE.Scene();
    this._smokeQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m.smoke);
    this._smokeQuad.frustumCulled = false;
    this._smokeScene.add(this._smokeQuad);
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m.composite);
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_W, LUM_H, {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType, depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(this._display.geometry, m.composite);
    lq.frustumCulled = false;
    this._lumScene.add(lq);
    this._lumPixels = new Uint8Array(LUM_W * LUM_H * 4);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._beatSeconds = 60 / FREE_BPM;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null; this._lastMode = null;
    this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._freeBeat = 0;
    this._sinceBeat = 10; this._sinceBar = 10; this._beatPhase = 0; this._barPhase = 0; this._beats = 0;
    this._low = 0.5; this._mid = 0.5; this._high = 0.5;
    this._drift = new THREE.Vector2(); this._drift2 = new THREE.Vector2(); this._warpT = new THREE.Vector2(17.3, 41.9);
    this._wind = WIND_UP - WIND_SIDE; this._windFrom = this._wind; this._windTo = this._wind; this._windK = 1;
    this._pairs = DEFAULT_PAIRS;
    this._pair = 0;
    this._colFrom = [new THREE.Color(), new THREE.Color()];
    this._colTo = [new THREE.Color(), new THREE.Color()];
    this._colK = 1;
    this._floor = this._forcedFloor === '1' ? 1 : 0;
    this._floorTarget = this._floor;
    this._density = 1;
    this._intensity = 1;
    this._ring2 = 0; this._ring2Grow = 0;
    this._dropAt = -1e9; this._dropUntil = -1; this._lastDropTime = -1e9; this._debugDropAt = 4;
    this._frame = 0; this._lastLumAt = 0;
    this._lastPaletteRef = undefined;
    this._cu.uGain.value = 1;
    this._su.uMode.value = this._mode;
    this._setPairNow(0);
    this._layoutRing();
    this._writeUniforms(1);
  }

  // --- colours ------------------------------------------------------------------------

  _pairColours(i, outA, outB) {
    const p = this._pairs[i % this._pairs.length];
    vdjColor(p[0], outA, LOUNGE.saturation);
    vdjColor(p[1], outB, LOUNGE.saturation);
  }

  _setPairNow(i) {
    this._pair = i;
    this._pairColours(i, this._colTo[0], this._colTo[1]);
    this._colFrom[0].copy(this._colTo[0]); this._colFrom[1].copy(this._colTo[1]);
    this._colK = 1;
  }

  _fadeToPair(i) {
    // from wherever the current fade is
    const k = smooth01(this._colK);
    this._colFrom[0].lerpColors(this._colFrom[0], this._colTo[0], k);
    this._colFrom[1].lerpColors(this._colFrom[1], this._colTo[1], k);
    this._pair = i;
    this._pairColours(i, this._colTo[0], this._colTo[1]);
    this._colK = 0;
  }

  _applyPalette(palette) {
    const v = palette && palette.vividColors && palette.vividColors.length >= 2 ? palette.vividColors : null;
    if (v) {
      // Pairs ordered by hue distance: a cover's two nearest colours (a pink
      // and an orange) make a gradient nobody sees going round.
      const n = Math.min(6, v.length);
      const hue = (c) => { const h = { h: 0, s: 0, l: 0 }; new THREE.Color(c).getHSL(h); return h.h; };
      const cand = [];
      for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
        const d = Math.abs(hue(v[i]) - hue(v[j]));
        cand.push({ a: v[i], b: v[j], d: Math.min(d, 1 - d) });
      }
      cand.sort((x, y) => y.d - x.d);
      const pairs = cand.filter((c) => c.d >= MIN_HUE_GAP).slice(0, 4).map((c) => [c.a, c.b]);
      // Too close everywhere: the strongest colour against its complement.
      if (!pairs.length) {
        const h = { h: 0, s: 0, l: 0 }; new THREE.Color(v[0]).getHSL(h);
        pairs.push([v[0], new THREE.Color().setHSL((h.h + 0.5) % 1, 0.9, 0.55).getHex()]);
      }
      this._pairs = pairs;
    } else {
      this._pairs = DEFAULT_PAIRS;
    }
    this._fadeToPair(0);
    this._colK = 1;
    this._colFrom[0].copy(this._colTo[0]); this._colFrom[1].copy(this._colTo[1]);
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  // --- geometry: the ring around the cover ------------------------------------------------

  _layoutRing() {
    // R19: the ring, its floor and its dark inside sit round the stage's centre
    // block, not the cover - only the smoke's safe-zone dimming follows the cover.
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    const hd = Math.hypot(hs.x, hs.y);
    let R = RING_OF_DIAG * hd;
    // Kept inside the frame's height (1.6 x the half-diagonal of a 16:9
    // overlay is taller than the frame), never inside the zone.
    R = Math.min(R, RING_FIT - Math.abs(c.y));
    R = Math.max(R, 1.05 * hd);
    this._R = R;
    this._su.uCenter.value.copy(c);
    this._su.uR.value = R;
    this._cu.uFloorY.value = c.y - R - 0.035;
  }

  // --- the frame ----------------------------------------------------------------------------

  update(deltaTime, profile) {
    const director = profile.director || {};
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._paused = profile.playbackState !== 'PLAYING';
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : FREE_BPM;
    this._beatSeconds = 60 / bpm;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = measured ? director.bands : null;

    // --- clocks ---
    let beatEdge = false, barEdge = false, phraseEdge = false, dBeats = 0;
    if (this._paused) dBeats = 0;
    else if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null) {
        let d = profile.beatPhase - this._lastBeatPhase;
        if (d < -0.5) { d += 1; beatEdge = true; }
        dBeats = Math.max(0, Math.min(d, 2 * dt / beat));
      }
      this._lastBeatPhase = profile.beatPhase;
      this._beatPhase = profile.beatPhase;
    } else {
      dBeats = dt / beat;
      this._freeBeat += dBeats;
      if (this._freeBeat >= 1) { this._freeBeat -= 1; beatEdge = true; }
      this._beatPhase = this._freeBeat;
    }
    this._beats += dBeats;
    this._sinceBeat += dt; this._sinceBar += dt;
    if (beatEdge) { this._beatCount++; this._sinceBeat = 0; }
    if (profile.barPhase != null && !this._paused) {
      if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) barEdge = true;
      this._lastBarPhase = profile.barPhase;
      this._barPhase = profile.barPhase;
    } else if (!this._paused) {
      if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
      this._barPhase = ((this._beatCount % 4) + this._beatPhase) / 4;
    }
    if (barEdge) { this._barCount++; this._sinceBar = 0; }
    const phrase = profile.phraseIndex;
    if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
    if (phrase == null && barEdge && this._barCount % 4 === 0) phraseEdge = true;
    if (phrase != null) this._lastPhrase = phrase;

    // --- bands, smoothed ---
    const kb = 1 - Math.exp(-dt / 0.4);
    const fl = 0.5 + 0.15 * Math.sin(this._time * 0.41), fm = 0.5 + 0.15 * Math.sin(this._time * 0.27 + 1), fh = 0.4 + 0.2 * Math.sin(this._time * 0.9 + 2);
    this._low += ((bands ? clamp01(bands.low || 0) : fl) - this._low) * kb;
    this._mid += ((bands ? clamp01(bands.mid || 0) : fm) - this._mid) * kb;
    this._high += ((bands ? clamp01(bands.high || 0) : fh) - this._high) * kb;

    // --- drop ---
    let dropNow = false;
    const ev = director.nextEvent;
    if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
    if (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0) dropNow = true;
    this._lastMode = director.mode;
    if (this._debugDrop && this._time >= this._debugDropAt) { dropNow = true; this._debugDropAt = this._time + 14 * 4 * beat; }
    if (dropNow && this._time - this._lastDropTime > 8 * beat) {
      this._lastDropTime = this._time;
      this._dropAt = this._beats;
      this._dropUntil = this._beats + (DROP_GROW_BARS + DROP_HOLD_BARS) * 4;
    }
    const inDrop = this._dropUntil > 0 && this._beats < this._dropUntil;
    if (this._dropUntil > 0 && !inDrop) this._dropUntil = -1;

    // --- phrases: one in two, a new pair of colours, the wind turns, the floor ---
    if (phraseEdge && !inDrop) {
      this._phraseCount++;
      if (this._phraseCount % 2 === 0) {
        this._fadeToPair(this._pair + 1);
        this._windFrom = this._wind;
        const side = (Math.random() < 0.5 ? -1 : 1) * WIND_SIDE * (0.6 + 0.4 * Math.random());
        this._windTo = WIND_UP + side;
        this._windK = 0;
        if (this._forcedFloor == null) this._floorTarget = Math.random() < 0.45 ? 1 : 0;
      }
    }
    const kBar = dBeats / 4;
    const toward = (x, target, k) => x + Math.max(-k, Math.min(k, target - x));
    if (this._colK < 1) this._colK = Math.min(1, this._colK + kBar);
    if (this._windK < 1) { this._windK = Math.min(1, this._windK + kBar / 2); this._wind = this._windFrom + (this._windTo - this._windFrom) * smooth01(this._windK); }
    this._floor = toward(this._floor, this._floorTarget, kBar);
    // The drop: the second ring grows from R to 1.3 R over two bars, stays
    // eight, then fades over one; the smoke thickens; +25 % over one bar.
    const sinceDrop = this._beats - this._dropAt;
    if (inDrop) {
      this._ring2Grow = smooth01(sinceDrop / (4 * DROP_GROW_BARS));
      this._ring2 = toward(this._ring2, 1, kBar * 2);
    } else {
      this._ring2 = toward(this._ring2, 0, kBar);
    }
    const densityTarget = (0.75 + 0.6 * this._low) * (inDrop ? 1.45 : 1);
    this._density += (densityTarget - this._density) * Math.min(1, dt / 1.5);
    const rise = inDrop ? 1 + LOUNGE.dropRise.gain * smooth01(sinceDrop / (4 * LOUNGE.dropRise.bars)) : 1;
    this._intensity = toward(this._intensity, rise, inDrop ? 1 : kBar * LOUNGE.dropRise.gain);

    // --- the smoke's drift: up and a little sideways, 5 % when paused ---
    const speed = DRIFT_SPEED * (0.6 + 0.8 * this._mid) * (this._paused ? 0.05 : 1);
    const wx = Math.cos(this._wind), wy = Math.sin(this._wind);
    this._drift.x = (this._drift.x + wx * speed * dt) % WRAP;
    this._drift.y = (this._drift.y + wy * speed * dt) % WRAP;
    this._drift2.x = (this._drift2.x + wx * speed * 0.45 * dt) % WRAP;
    this._drift2.y = (this._drift2.y + wy * speed * 0.45 * dt) % WRAP;
    const warpS = 0.05 * (this._paused ? 0.05 : 1);
    this._warpT.x = (this._warpT.x + warpS * 0.6 * dt) % WRAP;
    this._warpT.y = (this._warpT.y + warpS * dt) % WRAP;

    this._writeUniforms(dt);
    this._post.update(vdjBarChroma(this._sinceBar));
  }

  _writeUniforms(dt) {
    const su = this._su, cu = this._cu;
    const k = smooth01(this._colK);
    su.uColA.value.lerpColors(this._colFrom[0], this._colTo[0], k);
    su.uColB.value.lerpColors(this._colFrom[1], this._colTo[1], k);
    const a = 2 * Math.PI * vdjLoop(this._beats, TURN_BEATS);
    su.uRot.value.set(Math.cos(a), Math.sin(a));
    // The bar's wave: once round the ring in one bar, from the top.
    const wa = Math.PI / 2 - 2 * Math.PI * (this._barPhase || 0);
    su.uWaveDir.value.set(Math.cos(wa), Math.sin(wa));
    su.uWaveA.value = this._paused ? 0 : 1;
    su.uDrift.value.copy(this._drift);
    su.uDrift2.value.copy(this._drift2);
    su.uWarpT.value.copy(this._warpT);
    su.uDensity.value = this._density;
    const breath = 1 + (vdjBeatBreath(this._sinceBeat, LOUNGE) - 1) * (BREATH / LOUNGE.breath.gain);
    const flick = 1 + FLICKER * this._high * (0.6 * Math.sin(this._time * 23.0) * Math.sin(this._time * 7.1) + 0.4 * Math.sin(this._time * 41.0));
    cu.uFlicker.value = breath * flick;
    cu.uTubeT.value = (this._time * 0.0004) % 1;     // a tenth of a texel a second
    cu.uIntensity.value = this._intensity;
    cu.uR2.value = this._R * (1 + (RING2_SCALE - 1) * this._ring2Grow);
    cu.uRing2.value = this._ring2;
    cu.uFloor.value = this._floor;
  }

  _runPasses(renderer) {
    renderer.getDrawingBufferSize(_size);
    const W = Math.max(16, _size.x), H = Math.max(9, _size.y);
    const hs = parseFloat(urlParam('haloscale'));
    const sc = Number.isFinite(hs) && hs >= 0.25 && hs <= 1 ? hs : this._layout.scale;   // ?haloscale=<f> (bench)
    const w = Math.max(16, Math.round(W * sc)), h = Math.max(9, Math.round(H * sc));
    if (this._smokeTarget.width !== w || this._smokeTarget.height !== h) this._smokeTarget.setSize(w, h);
    this._su.uAspect.value = W / H;
    this._cu.uPxUnit.value = H / 2;
    this._layoutRing();
    const prev = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(this._smokeTarget);
    renderer.render(this._smokeScene, this._ortho);
    this._frame++;
    if (this._frame % LUM_EVERY === 0) {
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_W, LUM_H, this._lumPixels);
      let s = 0;
      const px = this._lumPixels;
      for (let i = 0; i < LUM_W * LUM_H; i++) {
        const l = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255;
        s += Math.pow(l, 1 / 2.2);
      }
      const dtS = this._time - this._lastLumAt;
      this._lastLumAt = this._time;
      this._cu.uGain.value = this._comfort.measure(s / (LUM_W * LUM_H), Math.max(0.05, dtS));
    }
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
  }

  get probe() {
    return {
      t: this._time, beats: this._beats, bar: this._barCount, barPhase: this._barPhase, phrase: this._phraseCount,
      R: this._R, center: this._su.uCenter.value.toArray(), R2: this._cu.uR2.value, ring2: this._ring2,
      floor: this._floor, floorY: this._cu.uFloorY.value, wind: this._wind, density: this._density,
      intensity: this._intensity, drop: this._dropUntil > 0, pair: this._pair, colK: this._colK,
      colA: this._su.uColA.value.toArray().map((v) => +v.toFixed(3)), colB: this._su.uColB.value.toArray().map((v) => +v.toFixed(3)),
      gain: this._cu.uGain.value, meanLum: this._comfort.average, lights: this._layout.lights, layers: this._layout.layers,
      smoke: [this._smokeTarget.width, this._smokeTarget.height], aberration: this._post.value.aberration
    };
  }

  dispose() {
    this._display.onBeforeRender = null;
    this._scene.remove(this._display);
    this._display.geometry.dispose();
    this._smokeQuad.geometry.dispose();
    this._smokeTarget.dispose();
    this._lumTarget.dispose();
    this._cu.uSmoke.value = null;
    this._scene = null;
  }
}
